/**
 * MentorSider — role-aware sidebar built on ThemedSiderV2 (B-2175).
 *
 * Админ — обычный сайдбар со всеми разделами. Все остальные состояния (ментор,
 * загрузка роли, отказ `/v1/auth/me`) — сайдбар только с разделом «ДЗ на
 * проверку»: он единственный доступен обеим ролям, поэтому «показать меньше»
 * ничего не ломает, а «показать больше» — нарушает требование «ментор видит
 * ТОЛЬКО проверку ДЗ». Полное меню появляется лишь когда роль **подтверждена**
 * как admin; раньше было наоборот — оно рисовалось, пока роль неизвестна.
 * Три состояния роли разбирает общий `useAppRole` (см. hooks/use-app-role).
 *
 * Механика фильтра. `ThemedSiderV2` принимает `render`:
 *   (props: { items: JSX.Element[]; logout: ReactNode; dashboard: ReactNode;
 *             collapsed: boolean }) => ReactNode
 * Каждый элемент `items` — это `<CanAccess resource={<имя ресурса>} …>` с
 * `key`, который Refine строит через `createResourceKey` как `` `/${identifier
 * ?? name}` `` (@refinedev/core 4.58: definitions/helpers/menu/create-resource-key.ts,
 * @refinedev/antd 5.47: themedLayoutV2/sider/index.tsx — `renderTreeView`).
 * То есть у пункта «ДЗ на проверку» key — `"/homework"`, а не `"homework"`:
 * фильтр по голому имени ресурса отсекал ВСЕ пункты и ментор видел пустое меню.
 *
 * Поэтому имя ресурса берём не из `key`, а из пропсов элемента (`resource` /
 * `params.resource.name`), а `key` оставлен запасным вариантом и нормализуется
 * от ведущих слэшей — фильтр переживёт смену формата ключа в Refine.
 *
 * Пункт «ЛК ученика» (B-2378) — под Logout у обеих подтверждённых ролей:
 * ментор и админ тоже могут учиться. Пока роль грузится или неизвестна, пункта
 * нет — как и остальных разделов, меню не показывает больше, чем подтверждено.
 *
 * Документированный паттерн кастомизации:
 * https://refine.dev/docs/ui-integrations/ant-design/components/themed-layout/#custom-sider
 */

import type { ReactNode } from "react";
import { UserOutlined } from "@ant-design/icons";
import { ThemedSiderV2 } from "@refinedev/antd";
import type { RefineThemedLayoutV2SiderProps } from "@refinedev/antd";
import { Menu } from "antd";

import { useAppRole } from "../hooks/use-app-role";
import { STUDENT_CABINET_URL } from "../lib/student-cabinet";

export const STUDENT_CABINET_LABEL = "ЛК ученика";

/** Ресурсы (`resources.ts` → `name`), которые ментор видит в меню. */
const MENTOR_ALLOWED_RESOURCES = new Set(["homework"]);

interface SiderRenderParams {
  items: JSX.Element[];
  logout: ReactNode;
  dashboard: ReactNode;
  collapsed: boolean;
}

/** Имя ресурса пункта меню — из пропсов `CanAccess`, с запасным вариантом по key. */
function resourceNameOf(item: JSX.Element): string {
  const props = item?.props as
    | { resource?: string; params?: { resource?: { name?: string } } }
    | undefined;
  const fromProps = props?.resource ?? props?.params?.resource?.name;
  if (fromProps) return fromProps;
  // Запасной путь: `/homework` → `homework`, `homework` → `homework`.
  const key = String(item?.key ?? "");
  return key.replace(/^\/+/, "").split("/").pop() ?? "";
}

/**
 * Пункт меню «ЛК ученика» — тем же `Menu.Item`, что и Logout у ThemedSiderV2.
 * Обычная ссылка, а не `navigate`: кабинет — другое приложение и другой origin.
 */
function studentCabinetItem(): JSX.Element {
  return (
    <Menu.Item key="student-cabinet" icon={<UserOutlined />}>
      <a href={STUDENT_CABINET_URL}>{STUDENT_CABINET_LABEL}</a>
    </Menu.Item>
  );
}

export function MentorSider(props: RefineThemedLayoutV2SiderProps): JSX.Element {
  const role = useAppRole();

  // Полное меню — только при подтверждённой роли админа. Порядок как у
  // дефолтного рендера ThemedSiderV2 (dashboard → items → logout) + пункт ЛК.
  if (role === "admin") {
    return (
      <ThemedSiderV2
        {...props}
        render={({ dashboard, items, logout }: SiderRenderParams) => (
          <>
            {dashboard}
            {items}
            {logout}
            {studentCabinetItem()}
          </>
        )}
      />
    );
  }

  // Ментор / загрузка / отказ: оставляем только разрешённые разделы.
  return (
    <ThemedSiderV2
      {...props}
      render={({ items, logout }: SiderRenderParams) => {
        const allowed = items.filter((item) => MENTOR_ALLOWED_RESOURCES.has(resourceNameOf(item)));
        return (
          <>
            {allowed}
            {logout}
            {role === "mentor" && studentCabinetItem()}
          </>
        );
      }}
    />
  );
}
